import moment, { Moment } from "moment";
import { MomentObject } from "../types/momentObject";
import { Culture } from "../types/culture";

const convertToDateTime = (dateTimeOffset?: Date | null | undefined): string | null => {
    let dto = typeof dateTimeOffset === "string" ? new Date(dateTimeOffset) : dateTimeOffset;
    return dto ? new Date(dto.getTime() - dto.getTimezoneOffset() * 60000).toISOString().replace("Z", "") : null;
};

const getDateFormat = (bUsDate: boolean): string => (bUsDate ? "MM/dd/yyyy hh:mm a" : "dd-MM-yyyy HH:mm");
const getDateOnlyFormat = (bUsDate: boolean): string => (bUsDate ? "MM/dd/yyyy" : "dd-MM-yyyy");

const getDateFormatByCulture = (culture: Culture) => getDateFormat(culture === "en-US");
const getDateOnlyFormatByCulture = (culture: Culture) => getDateOnlyFormat(culture === "en-US");

const momentDtoToLocalStringByCulture = (sDto: Date | Moment | MomentObject | string, culture: Culture): string =>
    sDto == null || sDto === undefined ? "_" : momentDtoToLocalString(sDto, culture === "en-US");

const momentDtoToLocalString = (sDto: Date | Moment | MomentObject | string, bUsDate: boolean): string => {
    if (sDto == null) return "";

    let m: Moment = moment(sDto);
    m.parseZone();

    if (bUsDate) return m.format("MM/DD/YYYY hh:mm a");
    return m.format("DD-MM-YYYY HH:mm");
};

const parseDateByCulture = (dateString: string, culture: Culture) => parseDate(dateString, culture === "en-US");
const parseMomentByCulture = (dateString: string, culture: Culture) => parseMoment(dateString, culture === "en-US");

const parseDate = (
    dateString: string,
    usFormat: Boolean
): {
    date: Date | null;
    isValid: Boolean;
    moment?: moment.Moment | null;
} => {
    if (usFormat) {
        let date = moment(dateString, "MM/DD/YYYY");
        let pattern = /^[0-9]{1,2}[/][0-9]{1,2}[/][0-9]{4}$/g;
        if (!date.isValid() || !pattern.test(dateString)) {
            return { date: null, isValid: false };
        }
        return { date: date.toDate(), moment: date, isValid: true };
    } else {
        let date = moment(dateString, "DD-MM-YYYY");
        let pattern = /^[0-9]{1,2}[-][0-9]{1,2}[-][0-9]{4}$/g;
        if (!date.isValid() || !pattern.test(dateString)) {
            return { date: null, isValid: false };
        }
        return { date: date.toDate(), moment: date, isValid: true };
    }
};

const parseMoment = (
    dateString: string,
    usFormat: Boolean
): {
    date: Date | null;
    isValid: Boolean;
    moment?: moment.Moment | null;
} => {
    if (usFormat) {
        let date = moment(dateString, "MM/DD/YYYY hh:mm a");
        let pattern = /^[0-9]{1,2}[/][0-9]{1,2}[/][0-9]{4}\s[0-9]{1,2}:[0-9]{1,2}\s(am|pm)$/gi;
        if (!date.isValid() || !pattern.test(dateString)) {
            return { date: null, isValid: false };
        }
        return { date: date.toDate(), moment: date, isValid: true };
    } else {
        let date = moment(dateString, "DD-MM-YYYY HH:mm");
        let pattern = /^[0-9]{1,2}[-][0-9]{1,2}[-][0-9]{4}\s[0-9]{1,2}:[0-9]{1,2}$/gi;
        if (!date.isValid() || !pattern.test(dateString)) {
            return { date: null, isValid: false };
        }
        return { date: date.toDate(), moment: date, isValid: true };
    }
};

const formatToTimeString24 = (momentOrDate: Date | Moment) => moment(momentOrDate).format("HH:mm");

const formatToTimeString12 = (momentOrDate: Date | Moment) => moment(momentOrDate).format("hh:mm");

const getNextSixAm = (): Moment => {
    let momentToday = moment();

    if (momentToday.hours() < 6) {
        momentToday.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
        return momentToday;
    } else {
        let momentTomorrow = momentToday.add(1, "day");
        momentTomorrow.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
        return momentTomorrow;
    }
};

const getSixAmDaysBack = (daysBack: number) => {
    let nextSixAm = getNextSixAm();
    nextSixAm.subtract(daysBack, "day");
    return nextSixAm;
};

const parseHoursMinutesIntoMomentObject = (hoursMinutesString: string) => {
    let hoursMinutes = hoursMinutesString.split(":");
    let hours = parseInt(hoursMinutes[0]);
    let minutes = parseInt(hoursMinutes[1]);
    let result = {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
    };

    if (!hoursMinutesString) {
        console.log("hoursMinutesString is null");
        return result;
    }

    console.log("hoursMinutesString is not null", result);

    result = {
        hours: hoursMinutesString.toLowerCase().includes("p") && hours <= 12 ? hours + 12 : hours,
        minutes: minutes,
        seconds: 0,
        milliseconds: 0
    };
};

const formatMomentToString = (momentFormat: Date | Moment | MomentObject) => moment(momentFormat).format("yyyyMMDDHHmm");

const formatDateOnlyToStringByCulture = (date: Date, culture: Culture): string =>
    moment(date).format(culture === "en-US" ? "MM/DD/YYYY" : "DD-MM-YYYY") as string;

const dateDiffSeconds = (earlerDate: Date, laterDate: Date) => (laterDate.getTime() - earlerDate.getTime()) / 1000;

export {
    convertToDateTime,
    momentDtoToLocalString,
    momentDtoToLocalStringByCulture,
    parseDateByCulture,
    parseMomentByCulture,
    getNextSixAm,
    getSixAmDaysBack,
    formatMomentToString,
    formatToTimeString24,
    formatToTimeString12,
    parseHoursMinutesIntoMomentObject,
    getDateFormat,
    getDateFormatByCulture,
    getDateOnlyFormatByCulture,
    formatDateOnlyToStringByCulture,
    dateDiffSeconds
};
